import { useState, type FormEvent } from 'react'
import {
  CAlert,
  CButton,
  CForm,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CSpinner,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilArrowLeft, cilLockLocked, cilUser } from '@coreui/icons'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth/AuthProvider'
import { apiErrorMessage, loadCsrfToken } from '../../api/client'
import banner from '../../assets/larice-identity-banner.webp'
import mark from '../../assets/larice-mark.png'

export function LoginPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  if (!auth.loading && auth.user) return <Navigate to="/console" replace />
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      await loadCsrfToken()
      await auth.login(username, password)
      const state = location.state as { from?: string } | null
      navigate(state?.from?.startsWith('/console') ? state.from : '/console', { replace: true })
    } catch (caught) {
      setError(apiErrorMessage(caught))
    } finally {
      setSubmitting(false)
    }
  }
  return (
    <main className="login-page">
      <div className="login-page__identity">
        <img src={banner} alt="LARICE Electrical and Computer Engineering" />
        <div>
          <span>Protected operations layer</span>
          <h1>FreeIPA member access</h1>
          <p>
            Public machine status remains available without signing in. This console exposes approved
            service launch links, operational records and audit workflows according to your FreeIPA
            groups.
          </p>
          <ul>
            <li>Passwords are verified by FreeIPA and are never stored here.</li>
            <li>Session cookies are HTTP-only and production requires HTTPS.</li>
            <li>Every public ledger publication requires independent review.</li>
          </ul>
        </div>
      </div>
      <div className="login-page__form">
        <Link to="/" className="back-link">
          <CIcon icon={cilArrowLeft} /> Return to public Observatory
        </Link>
        <div className="login-card">
          <img src={mark} alt="" />
          <span className="eyebrow">LARICE H100 Console</span>
          <h2>Sign in</h2>
          <p>Use your institutional FreeIPA username and password.</p>
          {error && <CAlert color="danger">{error}</CAlert>}
          <CForm onSubmit={submit}>
            <label htmlFor="username">Username</label>
            <CInputGroup>
              <CInputGroupText>
                <CIcon icon={cilUser} />
              </CInputGroupText>
              <CFormInput
                id="username"
                autoComplete="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                required
              />
            </CInputGroup>
            <label htmlFor="password">Password</label>
            <CInputGroup>
              <CInputGroupText>
                <CIcon icon={cilLockLocked} />
              </CInputGroupText>
              <CFormInput
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </CInputGroup>
            <CButton color="primary" size="lg" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <CSpinner size="sm" /> Signing in…
                </>
              ) : (
                'Continue with FreeIPA'
              )}
            </CButton>
          </CForm>
          <small>
            Contact the LARICE platform administrator if your group membership does not provide the
            expected access.
          </small>
        </div>
      </div>
    </main>
  )
}
